import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Search, ArrowUpRight, X, Check } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import KeywordSections from "@/components/sections/KeywordSections";
import { CATEGORIES } from "@/data/site";
import { submitEnquiry } from "@/lib/api";
import { trackLeadConversion } from "@/lib/analytics";
import { useSeo } from "@/lib/seo";
import { useEffect } from "react";

function EnquireDialog({ open, onClose, category }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) { setForm({ name: "", email: "", phone: "" }); setDone(false); }
  }, [open]);

  const handle = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitEnquiry({ ...form, category: category?.name || "General" });
      trackLeadConversion();
      setDone(true);
      toast.success("Enquiry received. We'll reply within 30 minutes on business hours.");
      setTimeout(onClose, 1600);
    } catch (err) {
      toast.error("Something went wrong. Please try WhatsApp or email.");
    } finally { setLoading(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center px-4"
          data-testid="enquire-dialog"
        >
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ y: 24, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 24, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.2, 0.7, 0.15, 1] }}
            className="relative bg-white rounded-3xl w-full max-w-lg p-8 md:p-10 border border-slate-100 shadow-2xl"
          >
            <button data-testid="enquire-close" onClick={onClose} className="absolute top-5 right-5 w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50">
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <div className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold">/ Enquire</div>
            <h3 className="mt-3 font-display text-3xl font-light tracking-tight text-slate-900">
              {category?.name}
            </h3>
            <p className="mt-2 text-sm text-slate-500">Share your details, we'll follow up with a scoped quote.</p>

            {done ? (
              <div className="mt-8 flex flex-col items-center text-center py-8">
                <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center">
                  <Check className="w-6 h-6 text-blue-600" strokeWidth={1.75} />
                </div>
                <div className="mt-4 font-display text-xl">Thanks, we've got it.</div>
                <div className="mt-1 text-sm text-slate-500">We'll be in touch shortly.</div>
              </div>
            ) : (
              <form onSubmit={handle} className="mt-6 space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">Name</label>
                  <input data-testid="enquire-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-2 w-full bg-transparent border-b border-slate-200 focus:border-slate-900 py-2 text-slate-900 text-base outline-none" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">Email</label>
                  <input data-testid="enquire-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-2 w-full bg-transparent border-b border-slate-200 focus:border-slate-900 py-2 text-slate-900 text-base outline-none" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">Phone</label>
                  <input data-testid="enquire-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-2 w-full bg-transparent border-b border-slate-200 focus:border-slate-900 py-2 text-slate-900 text-base outline-none" />
                </div>
                <button data-testid="enquire-submit" disabled={loading}
                  className="mt-6 group w-full inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 text-white px-6 py-4 text-sm font-medium hover:bg-blue-600 transition-colors disabled:opacity-60">
                  {loading ? "Sending…" : "Submit Enquiry"}
                  <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function Categories() {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);

  useSeo({
    title: "Database Categories — Stock Market, Real Estate, Loan, B2B, Telecalling Data | Datorra India",
    description:
      "Explore Datorra India's database categories — stock market traders, real estate customer data, loan and personal loan data, B2B database providers, mobile number database, telecalling and email databases across Pan India.",
    path: "/databases",
  });

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return CATEGORIES;
    return CATEGORIES.filter((c) => c.name.toLowerCase().includes(term) || c.desc.toLowerCase().includes(term));
  }, [q]);

  return (
    <div data-testid="categories-page">
      <PageHeader
        eyebrow="Databases"
        title="Every category,"
        italicWord="segmented for India."
        description="Twenty verticals, forty-plus filters. Pick a category, tell us your ICP, receive a scoped sample within 24 hours."
      />

      <section className="py-10 md:py-14 bg-white border-b border-slate-100 sticky top-16 md:top-20 z-30 backdrop-blur-xl bg-white/90">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 border-b border-slate-200 pb-3">
            <Search className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
            <input
              data-testid="category-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search databases — try 'GST', 'Doctors', 'Real Estate'"
              className="flex-1 bg-transparent outline-none text-base md:text-lg text-slate-900 placeholder:text-slate-400"
            />
            {q && (
              <button onClick={() => setQ("")} className="text-xs text-slate-500 hover:text-slate-900">Clear</button>
            )}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {filtered.length === 0 ? (
            <div className="text-center py-24">
              <div className="font-display text-2xl text-slate-900">No categories match "{q}"</div>
              <div className="mt-3 text-slate-500">Ask us — we build custom datasets too.</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {filtered.map((c, i) => {
                const Icon = c.icon;
                return (
                  <motion.article
                    key={c.key}
                    layout
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: (i % 3) * 0.05, duration: 0.5 }}
                    className="group relative bg-white border border-slate-100 rounded-3xl p-7 md:p-8 hover-lift hover:shadow-xl hover:shadow-slate-900/[0.05] hover:border-blue-100 flex flex-col justify-between overflow-hidden"
                    data-testid={`category-${c.key}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="w-11 h-11 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center transition-all duration-500 group-hover:bg-blue-600 group-hover:border-blue-600">
                        <Icon className="w-5 h-5 text-slate-700 transition-colors duration-500 group-hover:text-white" strokeWidth={1.5} />
                      </div>
                      <div className="text-[11px] uppercase tracking-[0.22em] text-slate-400 font-medium">/{String(i + 1).padStart(2, "0")}</div>
                    </div>
                    <div className="mt-12">
                      {["business", "bizloan"].includes(c.key) ? (
                        <Link to={c.key === "bizloan" ? "/business-loan-database" : "/business-owners-database"} className="font-display text-xl md:text-2xl font-medium text-slate-900 tracking-tight hover:text-blue-600 transition-colors block">
                          {c.name}
                        </Link>
                      ) : (
                        <div className="font-display text-xl md:text-2xl font-medium text-slate-900 tracking-tight">{c.name}</div>
                      )}
                      <div className="mt-3 text-sm text-slate-500 leading-relaxed">{c.desc}</div>
                      {c.key === "gstsp" && (
                        <Link to="/gst-sales-purchase-data" className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700">
                          Read full page <ArrowUpRight className="w-3 h-3" strokeWidth={2} />
                        </Link>
                      )}
                      {c.key === "newco" && (
                        <Link to="/mca-company-database" className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700">
                          Read full page <ArrowUpRight className="w-3 h-3" strokeWidth={2} />
                        </Link>
                      )}
                      {c.key === "gst" && (
                        <Link to="/gst-database" className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700">
                          Read full page <ArrowUpRight className="w-3 h-3" strokeWidth={2} />
                        </Link>
                      )}
                      {c.key === "business" && (
                        <Link to="/business-owners-database" className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700">
                          Read full page <ArrowUpRight className="w-3 h-3" strokeWidth={2} />
                        </Link>
                      )}
                      {c.key === "bizloan" && (
                        <Link to="/business-loan-database" className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700">
                          Read full page <ArrowUpRight className="w-3 h-3" strokeWidth={2} />
                        </Link>
                      )}
                    </div>
                    <button
                      data-testid={`enquire-btn-${c.key}`}
                      onClick={() => setActive(c)}
                      className="mt-8 group/btn inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white pl-4 pr-3 py-2.5 text-xs font-medium hover:bg-blue-600 transition-colors self-start"
                    >
                      Enquire Now
                      <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" strokeWidth={1.75} />
                    </button>
                  </motion.article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <EnquireDialog open={!!active} onClose={() => setActive(null)} category={active} />
      <KeywordSections />
    </div>
  );
}
